import React, { useState, useRef } from 'react'
import * as Immer from 'immer'
import classNames from 'classnames'
import { DataTable } from 'primereact/datatable'
import { Paginator } from 'primereact/paginator'
import { Column } from 'primereact/column'
import { Toast } from 'primereact/toast'
import { Button } from 'primereact/button'
import { Toolbar } from 'primereact/toolbar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { InputSwitch } from 'primereact/inputswitch'
import { Skeleton } from 'primereact/skeleton';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import {
  idBodyTemplate, nameBodyTemplate, typeBodyTemplate
} from '../components/ColumnBodyTemplate'
import { listAllCompaniesRequest, createCompanyRequest, editCpmpanyRequest, createBusinessRequest, editBusinessRequest } from '../apis/calyxSaaSDashboardAPI'
import {
  DEFAULT_USER_PREFERENCE_OPTION,
  TEMPERATURE_UNIT_OPTIONS,
  WIND_SPEED_UNIT_OPTIONS,
  WEIGHT_UNIT_OPTIONS,
  WATER_UNIT_OPTIONS,
  USER_PREFERENCE_LANGUAGE_OPTIONS,
} from '../constants/userPreferenceOptions'
import { DEFAULT_COMPANY_FEATURE_ENABLE_SETTINGS } from '../constants/companyFeatureEnableSettings'
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useStore } from '../stores/GlobalStore'
import axios from 'axios'
import { StatusCodes } from 'http-status-codes';

/**
 * @typedef {import('../apis/hey-api/types.gen').UserPreferenceOptionDto} UserPreferenceOptionDto
 */

/**
 * @typedef {{
 *   devicePage: {
 *     enableScaleStatus: boolean;
 *     enableScaleWeightData: boolean;
 *     enableScaleUniformityData: boolean;
 *   };
 *   farmOverviewPage: {
 *     enableScalePerformanceData: boolean;
 *   };
 * }} CompanyFeatureEnableSettings;
 */


/**
 * @type {React.FunctionComponent<{
 *   value?: CompanyFeatureEnableSettings;
 *   onChange?: (newValue: CompanyFeatureEnableSettings) => void;
 * }>}
 */
const InputCompanyFeatureEnableSettings = (props) => {
  const {
    value: companyFeatureEnableSettings,
    onChange: onCompanyFeatureEnableSettingsChange,
  } = props;

  if (companyFeatureEnableSettings == null) return (
    <InputText disabled value="N/A" />
  );

  return (
    <div className="flex flex-column gap-3 p-3" style={{ border: '1px solid #d1d5db', borderRadius: '6px' }}>
      <div>
        <label className="block font-semibold mb-2">Device Page</label>
        <div className="flex flex-column gap-2">
          <label className="flex align-items-center gap-2 cursor-pointer select-none">
            <InputSwitch
              checked={companyFeatureEnableSettings.devicePage.enableScaleStatus}
              onChange={(e) => onCompanyFeatureEnableSettingsChange?.(
                Immer.produce(companyFeatureEnableSettings, (_companyFeatureEnableSettings) => {
                  _companyFeatureEnableSettings.devicePage.enableScaleStatus = e.value;
                })
              )}
            />
            <span>Enable Scale Status</span>
          </label>
          <label className="flex align-items-center gap-2 cursor-pointer select-none">
            <InputSwitch
              checked={companyFeatureEnableSettings.devicePage.enableScaleWeightData}
              onChange={(e) => onCompanyFeatureEnableSettingsChange?.(
                Immer.produce(companyFeatureEnableSettings, (_companyFeatureEnableSettings) => {
                  _companyFeatureEnableSettings.devicePage.enableScaleWeightData = e.value;
                })
              )}
            />
            <span>Enable Scale Weight Data</span>
          </label>
          <label className="flex align-items-center gap-2 cursor-pointer select-none">
            <InputSwitch
              checked={companyFeatureEnableSettings.devicePage.enableScaleUniformityData}
              onChange={(e) => onCompanyFeatureEnableSettingsChange?.(
                Immer.produce(companyFeatureEnableSettings, (_companyFeatureEnableSettings) => {
                  _companyFeatureEnableSettings.devicePage.enableScaleUniformityData = e.value;
                })
              )}
            />
            <span>Enable Scale Uniformity Data</span>
          </label>
        </div>
      </div>
      <div>
        <label className="block font-semibold mb-2">Farm Overview Page</label>
        <div className="flex flex-column gap-2">
          <label className="flex align-items-center gap-2 cursor-pointer select-none">
            <InputSwitch
              checked={companyFeatureEnableSettings.farmOverviewPage.enableScalePerformanceData}
              onChange={(e) => onCompanyFeatureEnableSettingsChange?.(
                Immer.produce(companyFeatureEnableSettings, (_companyFeatureEnableSettings) => {
                  _companyFeatureEnableSettings.farmOverviewPage.enableScalePerformanceData = e.value;
                })
              )}
            />
            <span>Enable Scale Performance Data</span>
          </label>
        </div>
      </div>
    </div>
  );
};

/**
 * @type {React.FunctionComponent<{
 *   value?: UserPreferenceOptionDto;
 *   onChange?: (newValue: UserPreferenceOptionDto) => void;
 * }>}
 */
const InputDefaultUserPreferenceOption = (props) => {
  const { value, onChange } = props;
  const preference = value ?? DEFAULT_USER_PREFERENCE_OPTION;

  return (
    <div className="flex flex-column gap-3 p-3" style={{ border: '1px solid #d1d5db', borderRadius: '6px' }}>
      <div className="flex flex-column gap-1">
        <label className="font-semibold">Language</label>
        <Dropdown
          value={preference.language}
          options={USER_PREFERENCE_LANGUAGE_OPTIONS}
          onChange={(event) => onChange?.(
            Immer.produce(preference, (_preference) => {
              _preference.language = event.value;
            })
          )}
        />
      </div>
      <div className="flex gap-3">
        <div className="flex flex-column gap-1" style={{ flex: 1 }}>
          <label className="font-semibold">Weight Unit</label>
          <Dropdown
            value={preference.weightUnit}
            options={WEIGHT_UNIT_OPTIONS}
            onChange={(event) => onChange?.(
              Immer.produce(preference, (_preference) => {
                _preference.weightUnit = event.value;
              })
            )}
          />
        </div>
        <div className="flex flex-column gap-1" style={{ flex: 1 }}>
          <label className="font-semibold">Water Unit</label>
          <Dropdown
            value={preference.waterUnit}
            options={WATER_UNIT_OPTIONS}
            onChange={(event) => onChange?.(
              Immer.produce(preference, (_preference) => {
                _preference.waterUnit = event.value;
              })
            )}
          />
        </div>
      </div>
      <div className="flex gap-3">
        <div className="flex flex-column gap-1" style={{ flex: 1 }}>
          <label className="font-semibold">Temperature Unit</label>
          <Dropdown
            value={preference.temperatureUnit}
            options={TEMPERATURE_UNIT_OPTIONS}
            onChange={(event) => onChange?.(
              Immer.produce(preference, (_preference) => {
                _preference.temperatureUnit = event.value;
              })
            )}
          />
        </div>
        <div className="flex flex-column gap-1" style={{ flex: 1 }}>
          <label className="font-semibold">Wind Speed Unit</label>
          <Dropdown
            value={preference.windSpeedUnit}
            options={WIND_SPEED_UNIT_OPTIONS}
            onChange={(event) => onChange?.(
              Immer.produce(preference, (_preference) => {
                _preference.windSpeedUnit = event.value;
              })
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const CrudCompany = () => {
  const emptyCompany = {
    id: null,
    name: '',
    businesses: [],
    companyFeatureEnableSettings: DEFAULT_COMPANY_FEATURE_ENABLE_SETTINGS,
    defaultUserPreferenceOption: DEFAULT_USER_PREFERENCE_OPTION,
  }

  const queryClient = useQueryClient()
  const [createCompanyDialogVisibility, setCreateCompanyDialogVisibility] = useState(false)
  const [editCompanyDialogVisibility, setEditCompanyDialogVisibility] = useState(false)
  const [companyDetailDialogVisibility, setCompanyDetailDialogVisibility] = useState(false)
  const [createBusinessDialogVisibility, setCreateBusinessDialogVisibility] = useState(false)
  const [addBusinessForEditCompanyDialogVisibility, setAddBusinessForEditCompanyDialogVisibility] = useState(false)
  const [editBusinessForEditCompanyDialogVisibility, setEditBusinessForEditCompanyDialogVisibility] = useState(false)
  const [focusedCompany, setFocusedCompany] = useState(emptyCompany)
  const [newBusinessType, setNewBusinessType] = useState('broiler')
  const [goingToEditBusinessId, setGoingToEditBusinessId] = useState('')
  const { state, dispatch } = useStore()

  const [submitted, setSubmitted] = useState(false)
  const [companyGlobalFilter, setCompanyGlobalFilter] = useState(null)
  const [businessGlobalFilter, setBusinessGlobalFilter] = useState(null)
  const toast = useRef(null)
  const companyDataTable = useRef(null)
  const businessDataTable = useRef(null)
  const createBusinessDataTable = useRef(null)
  const editBusinessDataTable = useRef(null)
  const tmpBusinessIdRef = useRef(0)
  const businessTypeDropdown = [
    { label: 'broiler', value: 'broiler' },
    { label: 'layer', value: 'layer' },
    { label: 'swine', value: 'swine' },
    { label: 'generic', value: 'generic' },
  ]

  const { data: companies, isFetching, isError } = useQuery(['list', 'companies'], () => {
    return listAllCompaniesRequest()
  }, {
    refetchOnWindowFocus: false,
    retry: 0,
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
          let countDownSecond = 3;
          toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
          const logoutCountdownInterval = setInterval(() => {
            countDownSecond--;
            if (countDownSecond <= 0) {
              clearInterval(logoutCountdownInterval);
              dispatch({
                type: 'LOGOUT',
              })
            }
          }, 1000);
        } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
          toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
        } else {
          toast.current.show({ severity: 'error', summary: `Unknown Error, status code: ${err?.response?.status}`, life: 5000 });
        }
      } else {
        toast.current.show({ severity: 'error', summary: 'Unknown Error', life: 5000 });
      }
    }
  })

  const createCompanyMutation = useMutation(newCompany => {
    return createCompanyRequest(newCompany)
  },
    {
      onSuccess: (savedCompany) => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'New Company has been saved', life: 3000 })
        queryClient.setQueryData(['list', 'companies'], staleCompanies => {
          const newCompanies = [savedCompany, ...staleCompanies]
          return newCompanies
        })
        setCreateCompanyDialogVisibility(false);
        setNewBusinessType('broiler')
      },
      onError: (err) => {
        console.log('got error while creating company', err)
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            const logoutCountdownInterval = setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                clearInterval(logoutCountdownInterval);
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unknown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unknown Error', life: 5000 });
        }
      }
    })

    const editCompanyMutation = useMutation(editCompanyOption => {
      return editCpmpanyRequest(editCompanyOption.companyId, {
        name: editCompanyOption.newCompanyName,
        companyFeatureEnableSettings: editCompanyOption.newCompanyFeatureEnableSettings,
        defaultUserPreferenceOption: editCompanyOption.newDefaultUserPreferenceOption
      })
    },
      {
        onSuccess: (updatedCompany) => {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected company has been update', life: 3000 })
          queryClient.setQueryData(['list', 'companies'], staleCompanies => {
            const newCompanies = [...staleCompanies]
            const updateedCompanyIdx = newCompanies.findIndex( staleCompany => staleCompany.id === updatedCompany.id )
            if(updateedCompanyIdx >= 0){
              newCompanies[updateedCompanyIdx] = updatedCompany
              return newCompanies
            } else {
              toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
            }
          })
          setEditCompanyDialogVisibility(false);
        },
        onError: (err) => {
          console.log('got error while editing business', err)
          if (axios.isAxiosError(err)) {
            if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
              let countDownSecond = 3;
              toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
              const logoutCountdownInterval = setInterval(() => {
                countDownSecond--;
                if (countDownSecond <= 0) {
                  clearInterval(logoutCountdownInterval);
                  dispatch({
                    type: 'LOGOUT',
                  })
                }
              }, 1000);
            } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
              toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
            } else {
              toast.current.show({ severity: 'error', summary: `Unknown Error, status code: ${err?.response?.status}`, life: 5000 });
            }
          } else {
            toast.current.show({ severity: 'error', summary: 'Unknown Error', life: 5000 });
          }
        }
      })

    const editBusinessMutation = useMutation(editBusinessOption => {
      return editBusinessRequest(editBusinessOption.companyId, editBusinessOption.businessId, { type: editBusinessOption.newBusinessType})
    },
      {
        onSuccess: (updatedCompany) => {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected business has been update', life: 3000 })
          queryClient.setQueryData(['list', 'companies'], staleCompanies => {
            const newCompanies = [...staleCompanies]
            const updateedCompanyIdx = newCompanies.findIndex( staleCompany => staleCompany.id === updatedCompany.id )
            if(updateedCompanyIdx >= 0){
              newCompanies[updateedCompanyIdx] = updatedCompany
              return newCompanies
            } else {
              toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
            }
          })
          setFocusedCompany(updatedCompany)
          setEditBusinessForEditCompanyDialogVisibility(false);
          setNewBusinessType('broiler')
        },
        onError: (err) => {
          console.log('got error while editing business', err)
          if (axios.isAxiosError(err)) {
            if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
              let countDownSecond = 3;
              toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
              const logoutCountdownInterval = setInterval(() => {
                countDownSecond--;
                if (countDownSecond <= 0) {
                  clearInterval(logoutCountdownInterval);
                  dispatch({
                    type: 'LOGOUT',
                  })
                }
              }, 1000);
            } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
              toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
            } else {
              toast.current.show({ severity: 'error', summary: `Unknown Error, status code: ${err?.response?.status}`, life: 5000 });
            }
          } else {
            toast.current.show({ severity: 'error', summary: 'Unknown Error', life: 5000 });
          }
        }
      })

    const createBusinessMutation = useMutation( createBusinessOption => {
      return createBusinessRequest(createBusinessOption.companyId, { type: createBusinessOption.newBusinessType })
    },
      {
        onSuccess: (updatedCompany) => {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'New Business has been saved', life: 3000 })
          queryClient.setQueryData(['list', 'companies'], staleCompanies => {
            const newCompanies = [...staleCompanies]
            const updateedCompanyIdx = newCompanies.findIndex( staleCompany => staleCompany.id === updatedCompany.id )
            if(updateedCompanyIdx >= 0){
              newCompanies[updateedCompanyIdx] = updatedCompany
              return newCompanies
            }
          })
          setNewBusinessType('broiler')
          setAddBusinessForEditCompanyDialogVisibility(false)
          setFocusedCompany(updatedCompany)
        },
        onError: (err) => {
          if (axios.isAxiosError(err)) {
            if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
              let countDownSecond = 3;
              toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
              const logoutCountdownInterval = setInterval(() => {
                countDownSecond--;
                if (countDownSecond <= 0) {
                  clearInterval(logoutCountdownInterval);
                  dispatch({
                    type: 'LOGOUT',
                  })
                }
              }, 1000);
            } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
              toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
            } else {
              toast.current.show({ severity: 'error', summary: `Unknown Error, status code: ${err?.response?.status}`, life: 5000 });
            }
          } else {
            toast.current.show({ severity: 'error', summary: 'Unknown Error', life: 5000 });
          }
        }
      })

  const openAddNewBusinessDialogForCreateCompanyDialog = () => {
    setCreateBusinessDialogVisibility(true)
  }

  const openAddBusinessDialogForEditCompanyDialog = () => {
    setNewBusinessType('broiler')
    setAddBusinessForEditCompanyDialogVisibility(true)
  }

  const hideAddNewBusinessDialog = () => {
    setCreateBusinessDialogVisibility(false)
  }

  const openCreateCompanyDialog = () => {
    setSubmitted(false)
    setFocusedCompany(emptyCompany)
    setCreateCompanyDialogVisibility(true)
  }

  const hideCreateCompanyDialog = () => {
    setCreateCompanyDialogVisibility(false)
  }

  const hideEditCompanyDialog = () => {
    setEditCompanyDialogVisibility(false)
  }

  const hideCreateBusinessDialog = () => {
    setCreateBusinessDialogVisibility(false)
  }

  const hideEditBusinessForEditCompanyDialog = () => {
    setEditBusinessForEditCompanyDialogVisibility(false)
  }

  const hideAddBusinessForEditCompanyDialog = () => {
    setAddBusinessForEditCompanyDialogVisibility(false)
  }

  const hideDetailDialog = () => {
    setCompanyDetailDialogVisibility(false)
  }

  const handleAddNewBusiness = () => {
    const newFocusedCompany = { ...focusedCompany }
    newFocusedCompany.businesses.unshift({ id: `tmp-${tmpBusinessIdRef.current++}`, type: newBusinessType })
    setFocusedCompany(newFocusedCompany)
    setCreateBusinessDialogVisibility(false)
    setNewBusinessType('broiler')
  }

  const handleEditBusinessForEditCompanyDialog = () => {
    editBusinessMutation.mutate({
      companyId: focusedCompany.id,
      businessId: goingToEditBusinessId,
      newBusinessType: newBusinessType
    })
  }

  const handleAddBusinessForEditCompanyDialog = () => {
    createBusinessMutation.mutate({
      companyId: focusedCompany.id,
      newBusinessType
    })
  }

  const handleCreateCompany = () => {
    setSubmitted(true)
    if (!focusedCompany.name) {
      toast.current.show({ severity: 'error', summary: "new company name is required.", life: 2500 })
      return
    }
    const createCompanyRequestBody = {
      name: focusedCompany.name,
      businesses: focusedCompany.businesses.map(business => business.type),
      companyFeatureEnableSettings: focusedCompany.companyFeatureEnableSettings,
      defaultUserPreferenceOption: focusedCompany.defaultUserPreferenceOption,
    }
    createCompanyMutation.mutate(createCompanyRequestBody)
  }

  const handleEditCompany = () => {
    setSubmitted(true)
    if (!focusedCompany.name) {
      toast.current.show({ severity: 'error', summary: "company name should not be empty.", life: 2500 })
      return
    }
    editCompanyMutation.mutate({
      companyId: focusedCompany.id,
      newCompanyName: focusedCompany.name,
      newCompanyFeatureEnableSettings: focusedCompany.companyFeatureEnableSettings,
      newDefaultUserPreferenceOption: focusedCompany.defaultUserPreferenceOption
    })
  }

  const editCompany = (company) => {
    setSubmitted(false)
    setFocusedCompany({
      ...company,
      companyFeatureEnableSettings: company.companyFeatureEnableSettings ?? DEFAULT_COMPANY_FEATURE_ENABLE_SETTINGS,
      defaultUserPreferenceOption: company.defaultUserPreferenceOption ?? DEFAULT_USER_PREFERENCE_OPTION,
    })
    setEditCompanyDialogVisibility(true)
  }

  const removeTmpBusinessWhileCreateCompany = (businessGoingToRemove) => {
    const newFocusedCompany = { ...focusedCompany }
    newFocusedCompany.businesses = newFocusedCompany.businesses.filter(business => business.id !== businessGoingToRemove.id)
    setFocusedCompany(newFocusedCompany)
  }

  const showEditBusinessForEditCompanyDialog = (businessGoingToEdit) => {
    setNewBusinessType(businessGoingToEdit.type)
    setGoingToEditBusinessId(businessGoingToEdit.id)
    setEditBusinessForEditCompanyDialogVisibility(true)
  }

  const showCompanyDetail = (company) => {
    setFocusedCompany({ ...company })
    setCompanyDetailDialogVisibility(true)
  }

  const exportCSV = () => {
    companyDataTable.current.exportCSV()
  }

  const onNewCompanyNameInputChange = (e, name) => {
    const val = (e.target && e.target.value) || ''
    const _company = { ...focusedCompany }
    _company[`${name}`] = val

    setFocusedCompany(_company)
  }

  const onNewBusinessTypeInputChange = (e) => {
    const val = (e.target && e.target.value) || ''
    setNewBusinessType(val)
  }

  const leftToolbarTemplate = () => {
    return (
      <>
        <div className='my-2'>
          <Button label='New' icon='pi pi-plus' className='p-button-success mr-2' data-test-id='createNewCompany' onClick={openCreateCompanyDialog} />
        </div>
      </>
    )
  }

  const rightToolbarTemplate = () => {
    return (
      <>
        <Button label='Export' icon='pi pi-upload' className='p-button-help' onClick={exportCSV} />
      </>
    )
  }

  const createNewBusinessTemplate = () => {
    return (
      <>
        <div className='my-2'>
          <Button label='Add New Business To New Company' icon='pi pi-plus' className='p-button-success mr-2' data-test-id='addNewBusiness' onClick={openAddNewBusinessDialogForCreateCompanyDialog} />
        </div>
      </>
    )
  }

  const addBusinessForEditCompanyDialogTemplate = () => {
    return (
      <>
        <div className='my-2'>
          <Button label='Add New Business To Current Company' icon='pi pi-plus' className='p-button-success mr-2' onClick={openAddBusinessDialogForEditCompanyDialog} />
        </div>
      </>
    )
  }

  const companyActionBodyTemplate = (rowData) => {
    return (
      <div className='actions'>
        <Button tooltip="detail" icon='pi pi-info-circle' className='p-button-rounded p-button-info mr-2' onClick={() => showCompanyDetail(rowData)} />
        <Button tooltip="edit" icon='pi pi-pencil' className='p-button-rounded p-button-success mr-2' onClick={() => editCompany(rowData)} />
      </div>
    )
  }

  const createNewCompanyActionBodyTemplate = (rowData) => {
    return (
      <div className='Remove'>
        <Button tooltip="remove business" icon='pi pi-times' className='p-button-rounded p-button-danger mr-2' onClick={() => removeTmpBusinessWhileCreateCompany(rowData)} />
      </div>
    )
  }

  const editCompanyActionBodyTemplate = (rowData) => {
    return (
      <div className='Remove'>
        <Button tooltip="edit business" icon='pi pi-pencil' className='p-button-rounded p-button-success mr-2' onClick={() => showEditBusinessForEditCompanyDialog(rowData)} />
      </div>
    )
  }

  const companyDataTableHeader = (
    <div className='flex flex-column md:flex-row md:justify-content-between md:align-items-center'>
      <h5 className='m-0'>Manage Companies & Businesses </h5>
      <IconField iconPosition="left" className='block mt-2 md:mt-0'>
        <InputIcon className='pi pi-search' />
        <InputText type='search' data-test-id='searchCompany' onInput={(e) => setCompanyGlobalFilter(e.target.value)} placeholder='Search...' />
      </IconField>
    </div>
  )

  const businessDataTableHeader = (
    <div className='flex flex-column md:flex-row md:justify-content-between md:align-items-center'>
      <h5 className='m-0'>Businesses</h5>
    </div>
  )

  const createNewBusinessFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideCreateBusinessDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' data-test-id='saveNewBusiness' onClick={handleAddNewBusiness} />
    </>
  )

  const addBusinessForEditCompanyDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideAddBusinessForEditCompanyDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' onClick={handleAddBusinessForEditCompanyDialog} />
    </>
  )

  const editBusinessForEditCompanyDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideEditBusinessForEditCompanyDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' onClick={handleEditBusinessForEditCompanyDialog} />
    </>
  )

  const companyDetailDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideDetailDialog} />
    </>
  )

  const createCompanyDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideCreateCompanyDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' data-test-id='saveNewCompany' onClick={handleCreateCompany} />
    </>
  )

  const editCompanyDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideEditCompanyDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' onClick={handleEditCompany} />
    </>
  )

  // const [basicFirst, setBasicFirst] = useState(0);
  // const [basicRows, setBasicRows] = useState(10);

  {/* TODO: 後續有需要可以用自訂義的paginator做pagination */ }
  // const onBasicPageChange = (event) => {
  //   console.log("onBasicPageChange", event);
  //   setBasicFirst(event.first);
  //   setBasicRows(event.rows);
  // };

  return (
    <div className='grid crud-demo'>
      <div className='col-12'>
        <div className='card'>
          <Toast ref={toast} />
          <Toolbar className='mb-4' left={leftToolbarTemplate} right={rightToolbarTemplate} />

          {isFetching || isError ? (
            <DataTable value={Array.from({ length: 10 })} className="p-datatable-striped company-table-skeleton" header={companyDataTableHeader}>
              <Column field="id" header="id" style={{ width: '33%' }} body={<Skeleton />}></Column>
              <Column field="name" header="Name" style={{ width: '33%' }} body={<Skeleton />}></Column>
              <Column field="Action" header="Category" style={{ width: '33%' }} body={<Skeleton />}></Column>
            </DataTable>
          ) : (<>
            <DataTable
              ref={companyDataTable} value={companies}
              dataKey='id' paginator={true} rows={20} rowsPerPageOptions={[20, 10, 5]} className='datatable-responsive'
              paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
              currentPageReportTemplate='Showing {first} to {last} of {totalRecords} companies'
              globalFilter={companyGlobalFilter} emptyMessage='No companies found.' header={companyDataTableHeader} responsiveLayout='scroll'
            >
              <Column field='id' header='id' sortable body={idBodyTemplate} headerStyle={{ width: '33%', minWidth: '10rem' }} />
              <Column field='name' header='Name' sortable body={nameBodyTemplate} headerStyle={{ width: '33%', minWidth: '10rem' }} />
              <Column header='Actions' headerStyle={{ width: '33%', minWidth: '10rem' }} body={companyActionBodyTemplate} />
            </DataTable>
            {/* TODO: 後續有需要可以用自訂義的paginator做pagination */}
            {/* <Paginator
              first={basicFirst}
              rows={basicRows}
              totalRecords={120}
              rowsPerPageOptions={[10, 20, 30]}
              onPageChange={onBasicPageChange}
            >
            </Paginator> */}
          </>
          )}


          {/* create company dialog */}
          <Dialog blockScroll={true} visible={createCompanyDialogVisibility} style={{ width: '750px' }} header='Create New Company' modal className='p-fluid' footer={createCompanyDialogFooter} onHide={hideCreateCompanyDialog}>
            <div className='field'>
              <label htmlFor='name'>Name</label>
              <InputText id='name' data-test-id='typeCompanyName' value={focusedCompany.name} onChange={(e) => onNewCompanyNameInputChange(e, 'name')} required autoFocus className={classNames({ 'p-invalid': submitted && !focusedCompany.name })} />
              {!focusedCompany.name && <small className='p-invalid' style={{ color: 'red' }}>Name is required.</small>}
            </div>
            <div className='field'>
              <label>Feature Enable Settings</label>
              <InputCompanyFeatureEnableSettings
                value={focusedCompany.companyFeatureEnableSettings}
                onChange={(newValue) => setFocusedCompany((prev) => ({ ...prev, companyFeatureEnableSettings: newValue }))}
              />
            </div>
            <div className='field'>
              <label>Default User Preference Option</label>
              <InputDefaultUserPreferenceOption
                value={focusedCompany.defaultUserPreferenceOption}
                onChange={(newValue) => setFocusedCompany((prev) => ({ ...prev, defaultUserPreferenceOption: newValue }))}
              />
            </div>
            <Divider />
            <Toolbar className='mb-4' left={createNewBusinessTemplate} />
            <DataTable
              ref={createBusinessDataTable} value={focusedCompany.businesses}
              dataKey='id' paginator={true} rows={20} rowsPerPageOptions={[20, 10, 5]} className='datatable-responsive'
              paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
              currentPageReportTemplate='Showing {first} to {last} of {totalRecords} businesses'
              globalFilter={businessGlobalFilter} emptyMessage='Empty business.' header={businessDataTableHeader} responsiveLayout='scroll'
            >
              <Column field='id' header='id' sortable body={idBodyTemplate} headerStyle={{ width: '33.3%', minWidth: '10rem' }} />
              <Column field='type' header='Type' sortable body={typeBodyTemplate} headerStyle={{ width: '33.3%', minWidth: '10rem' }} />
              <Column header='Actions' headerStyle={{ width: '33.3%', minWidth: '10rem' }} body={createNewCompanyActionBodyTemplate} />
            </DataTable>
          </Dialog>

          {/* company detail dialog */}
          <Dialog blockScroll={true} visible={companyDetailDialogVisibility} style={{ width: '750px' }} header='Company' modal className='p-fluid' footer={companyDetailDialogFooter} onHide={hideDetailDialog}>
            <div className='field'>
              <label htmlFor='name' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Company Name: </label> <label>{focusedCompany.name}</label>
            </div>
            <DataTable
              ref={businessDataTable} value={focusedCompany.businesses}
              dataKey='id' paginator={true} rows={20} rowsPerPageOptions={[20, 10, 5]} className='datatable-responsive'
              paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
              currentPageReportTemplate='Showing {first} to {last} of {totalRecords} businesses'
              globalFilter={businessGlobalFilter} emptyMessage='No business found.' header={businessDataTableHeader} responsiveLayout='scroll'
            >
              <Column field='id' header='id' sortable body={idBodyTemplate} headerStyle={{ width: '50%', minWidth: '10rem' }} />
              <Column field='type' header='Type' sortable body={typeBodyTemplate} headerStyle={{ width: '50%', minWidth: '10rem' }} />
            </DataTable>
          </Dialog>

          {/* create business dialog in create company dialog */}
          <Dialog blockScroll={true} visible={createBusinessDialogVisibility} style={{ width: '350px' }} header='Create New Business' modal className='p-fluid' footer={createNewBusinessFooter} onHide={hideAddNewBusinessDialog}>
            <div className='field' data-test-id='chooseNewBusiness'>
              <label htmlFor='type'>Type</label>
              <Dropdown
                id='type' value={newBusinessType}
                disabled={createBusinessMutation.isLoading}
                options={businessTypeDropdown}
                onChange={(e) => onNewBusinessTypeInputChange(e)} required autoFocus
              />
            </div>
          </Dialog>

          {/* edit company dialog */}
          <Dialog blockScroll={true} visible={editCompanyDialogVisibility} style={{ width: '750px' }} header='Edit Company' modal className='p-fluid' footer={editCompanyDialogFooter} onHide={hideEditCompanyDialog}>
            <div className='field'>
              <label htmlFor='name'>Name</label>
              <InputText id='name' value={focusedCompany.name} onChange={(e) => onNewCompanyNameInputChange(e, 'name')} required autoFocus className={classNames({ 'p-invalid': submitted && !focusedCompany.name })} />
              {!focusedCompany.name && <small className='p-invalid' style={{ color: 'red' }}>Name is required.</small>}
            </div>
            <div className='field'>
              <label>Feature Enable Settings</label>
              <InputCompanyFeatureEnableSettings
                value={focusedCompany.companyFeatureEnableSettings}
                onChange={(newValue) => setFocusedCompany((prev) => ({ ...prev, companyFeatureEnableSettings: newValue }))}
              />
            </div>
            <div className='field'>
              <label>Default User Preference Option</label>
              <InputDefaultUserPreferenceOption
                value={focusedCompany.defaultUserPreferenceOption}
                onChange={(newValue) => setFocusedCompany((prev) => ({ ...prev, defaultUserPreferenceOption: newValue }))}
              />
            </div>
            <Divider />
            <Toolbar className='mb-4' left={addBusinessForEditCompanyDialogTemplate} />
            <DataTable
              ref={editBusinessDataTable} value={focusedCompany.businesses}
              dataKey='id' paginator={true} rows={20} rowsPerPageOptions={[20, 10, 5]} className='datatable-responsive'
              paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
              currentPageReportTemplate='Showing {first} to {last} of {totalRecords} businesses'
              globalFilter={businessGlobalFilter} emptyMessage='Empty business.' header={businessDataTableHeader} responsiveLayout='scroll'
            >
              <Column field='id' header='id' sortable body={idBodyTemplate} headerStyle={{ width: '33.3%', minWidth: '10rem' }} />
              <Column field='type' header='Type' sortable body={typeBodyTemplate} headerStyle={{ width: '33.3%', minWidth: '10rem' }} />
              <Column header='Actions' headerStyle={{ width: '33.3%', minWidth: '10rem' }} body={editCompanyActionBodyTemplate} />
            </DataTable>
          </Dialog>

          {/* add business dialog in edit company dialog */}
          <Dialog blockScroll={true} visible={addBusinessForEditCompanyDialogVisibility} style={{ width: '350px' }} header='Add Business' modal className='p-fluid' footer={addBusinessForEditCompanyDialogFooter} onHide={hideAddBusinessForEditCompanyDialog}>
            <div className='field'>
              <label htmlFor='type'>Type</label>
              <Dropdown
                id='type'
                value={newBusinessType}
                disabled={createBusinessMutation.isLoading}
                options={businessTypeDropdown}
                onChange={(e) => onNewBusinessTypeInputChange(e)} required autoFocus
              />
            </div>
          </Dialog>

          {/* edit business dialog in edit company dialog */}
          <Dialog blockScroll={true} visible={editBusinessForEditCompanyDialogVisibility} style={{ width: '350px' }} header='Edit Business' modal className='p-fluid' footer={editBusinessForEditCompanyDialogFooter} onHide={hideEditBusinessForEditCompanyDialog}>
            <div className='field'>
              <label htmlFor='type'>Type</label>
              <Dropdown
                id='type' value={newBusinessType}
                disabled={editBusinessMutation.isLoading}
                options={businessTypeDropdown}
                onChange={(e) => onNewBusinessTypeInputChange(e)} required autoFocus
              />
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
